import { SPAN_OVERLOAD_THRESHOLD } from '../../../domains/orgchart/assistant/orgAnalyticLensRules';
export interface ToolMatch {
  toolName: string;
  params: Record<string, unknown>;
  confidence: number;
}

interface StructuredMatchOptions {
  toolPriority?: string[];
  availableTools?: string[];
}

interface PatternRule {
  regex: RegExp;
  toolName: string;
  extractParams: (match: RegExpMatchArray) => Record<string, unknown>;
  confidence: number;
}

const normalizeColorByField = (rawField: string): string => {
  const normalized = rawField.trim().toLowerCase();
  if (normalized === 'gender' || normalized === 'genders') return 'sex';
  return rawField.trim();
};

const extractProcessColorTargetParams = (rawTarget: string, rawColor: string): Record<string, unknown> => {
  const target = rawTarget.trim();
  const normalizedTarget = target.toLowerCase();
  if (normalizedTarget === 'selected' || normalizedTarget === 'this') {
    return { color: rawColor.trim(), scope: 'selected' };
  }
  if (normalizedTarget === 'all') {
    return { color: rawColor.trim(), scope: 'all' };
  }
  return { color: rawColor.trim(), target };
};

export class StructuredToolMatcher {
  private patterns: PatternRule[] = [
    {
      regex: /^(?:find|show|highlight) overloaded managers[.!]?$/i,
      toolName: 'filter',
      extractParams: () => ({ field: 'directReports', operator: 'greater_than', value: String(SPAN_OVERLOAD_THRESHOLD) }),
      confidence: 0.98,
    },
    {
      regex: /^show managers with more than (\d+) direct reports[.!]?$/i,
      toolName: 'filter',
      extractParams: (match) => ({ field: 'directReports', operator: 'greater_than', value: match[1] }),
      confidence: 0.98,
    },

    {
      regex: /^clear (filters?|rules?|highlights?)$/i,
      toolName: 'clear_rules',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    // ─── Process / flowchart shortcuts (Issue #1640) ─────────────
    {
      regex: /^find\s+bottlenecks?(?:\s+in\s+this\s+process)?\.?$/i,
      toolName: 'process_critical_path',
      extractParams: () => ({}),
      confidence: 0.95,
    },
    {
      regex: /^(?:show|find)\s+(?:the\s+)?critical\s+path\.?$/i,
      toolName: 'process_critical_path',
      extractParams: () => ({}),
      confidence: 0.92,
    },
    {
      regex: /^(?:create|draft|generate|prepare|show(?:\s+me)?|give\s+me)\s+(?:(?:a|the)\s+)?(?:concise\s+)?(?:management\s+summary|leadership\s+brief)(?:\s+.*)?\.?$/i,
      toolName: 'process_management_summary',
      extractParams: () => ({ focus: 'management' }),
      confidence: 0.96,
    },
    {
      regex: /^(?:run\s+)?(?:a\s+)?structure(?:-only)?\s+(?:scan|review)(?:\s+of\s+this\s+process)?(?:\..*)?$/i,
      toolName: 'process_management_summary',
      extractParams: () => ({ focus: 'structure' }),
      confidence: 0.94,
    },
    {
      regex: /^(?:find|list|show)\s+(?:the\s+)?(?:missing\s+)?(?:process\s+)?fields?(?:\s+.*)?$/i,
      toolName: 'process_management_summary',
      extractParams: () => ({ focus: 'fields' }),
      confidence: 0.92,
    },
    {
      regex: /^(?:list|find|show)\s+(?:owner\s+actions?|ownership\s+gaps?|missing\s+owners?|unassigned\s+steps?)(?:\s+.*)?\.?$/i,
      toolName: 'process_role_analysis',
      extractParams: () => ({ query: 'unassigned' }),
      confidence: 0.96,
    },
    {
      regex: /^find\s+process\s+steps\s+with\s+missing\s+owners?\s+or\s+unclear\s+accountability\.?$/i,
      toolName: 'process_role_analysis',
      extractParams: () => ({ query: 'unassigned' }),
      confidence: 0.96,
    },
    {
      regex: /^(?:find|highlight|show|list)\s+(?:risky\s+)?handoffs?(?:\s+.*)?\.?$/i,
      toolName: 'process_role_analysis',
      extractParams: () => ({ query: 'handoffs' }),
      confidence: 0.94,
    },
    {
      regex: /^(?:(?:tidy|clean\s+up|auto[-\s]?layout|apply\s+(?:auto[-\s]?)?layout)(?:\s+(?:this|the))?(?:\s+(?:diagram|process|flowchart|layout))?|layout\s+(?:this|the)\s+(?:diagram|process|flowchart))\.?$/i,
      toolName: 'flow_layout',
      extractParams: () => ({ strategy: 'left-to-right' }),
      confidence: 0.95,
    },
    // ─── Compass shortcuts ───────────────────────────────────────
    {
      regex: /^(run|evaluate|check)\s+(compliance|soc\s*2|soc2|iso\s*27001|iso27001|hipaa|nist)$/i,
      toolName: 'compliance_evaluate',
      extractParams: (match) => {
        const fw = match[2].toLowerCase().replace(/\s+/g, '');
        const framework =
          fw === 'soc2' || fw === 'soc' ? 'soc2'
          : fw === 'iso27001' || fw === 'iso' ? 'iso27001'
          : fw === 'hipaa' ? 'hipaa'
          : fw === 'nist' ? 'nist'
          : 'soc2';
        return { framework };
      },
      confidence: 0.9,
    },
    {
      regex: /^(run|evaluate|check)\s+compliance(?:\s+against\s+(\S+))?$/i,
      toolName: 'compliance_evaluate',
      extractParams: (match) => ({ framework: (match[2] ?? 'soc2').toLowerCase() }),
      confidence: 0.85,
    },
    {
      regex: /^(find|flag|show)\s+risks?$/i,
      toolName: 'find_risks',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    {
      regex: /^find\s+sla\s+risks?$/i,
      toolName: 'find_risks',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    {
      regex: /^(find|show)\s+(gaps?|missing\s+evidence|control\s+gaps?)$/i,
      toolName: 'flag_control_gaps',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    {
      regex: /^where\s+am\s+i\s+missing\s+evidence$/i,
      toolName: 'flag_control_gaps',
      extractParams: () => ({}),
      confidence: 0.85,
    },
    {
      regex: /^export\s+(audit\s+)?(pdf|bundle|zip|oscal)$/i,
      toolName: 'export_audit_bundle',
      extractParams: (match) => {
        const fmt = match[2].toLowerCase();
        const format =
          fmt === 'pdf' ? 'pdf'
          : fmt === 'zip' || fmt === 'bundle' ? 'rdm-csv-zip'
          : fmt === 'oscal' ? 'oscal-assessment-results'
          : 'pdf';
        return { format };
      },
      confidence: 0.9,
    },
    {
      regex: /^download\s+(audit\s+)?(pdf|bundle|zip|oscal)$/i,
      toolName: 'export_audit_bundle',
      extractParams: (match) => {
        const fmt = match[2].toLowerCase();
        const format =
          fmt === 'pdf' ? 'pdf'
          : fmt === 'zip' || fmt === 'bundle' ? 'rdm-csv-zip'
          : 'oscal-assessment-results';
        return { format };
      },
      confidence: 0.85,
    },
    {
      regex: /^clear focus$/i,
      toolName: 'focus_view',
      extractParams: () => ({ action: 'clear' }),
      confidence: 0.9,
    },
    {
      regex: /^focus(?: on)? department (.+)$/i,
      toolName: 'focus_view',
      extractParams: (match) => ({ action: 'department', value: match[1].trim() }),
      confidence: 0.9,
    },
    {
      regex: /^focus(?: on)? (?:branch|team|subtree) (.+)$/i,
      toolName: 'focus_view',
      extractParams: (match) => ({ action: 'branch', value: match[1].trim() }),
      confidence: 0.9,
    },
    {
      regex: /^expand(?: one)? level$/i,
      toolName: 'branch_depth',
      extractParams: () => ({ action: 'expand_level' }),
      confidence: 0.9,
    },
    {
      regex: /^expand (?:branches?\s+)?(?:by\s+)?(?:1|one)\s+level$/i,
      toolName: 'branch_depth',
      extractParams: () => ({ action: 'expand_level' }),
      confidence: 0.9,
    },
    {
      regex: /^collapse(?: one)? level$/i,
      toolName: 'branch_depth',
      extractParams: () => ({ action: 'collapse_level' }),
      confidence: 0.9,
    },
    {
      regex: /^collapse (?:branches?\s+)?(?:by\s+)?(?:1|one)\s+level$/i,
      toolName: 'branch_depth',
      extractParams: () => ({ action: 'collapse_level' }),
      confidence: 0.9,
    },
    {
      regex: /^expand all(?: branches)?$/i,
      toolName: 'branch_depth',
      extractParams: () => ({ action: 'expand_all' }),
      confidence: 0.9,
    },
    {
      regex: /^color\s+(?:the\s+|a\s+|an\s+)?(.+?)\s+(?:department|dept|team)(?:\s+(?:to|in|with))?\s+(.+)$/i,
      toolName: 'color_match',
      extractParams: (match) => ({
        field: 'department',
        value: match[1].trim().replace(/^(?:the|a|an)\s+/i, ''),
        color: match[2].trim().replace(/^(?:to|in|with)\s+/i, ''),
      }),
      confidence: 0.96,
    },
    {
      regex: /^color\s+(?:the\s+|a\s+|an\s+)?(?:department|dept|team)\s+(.+?)(?:\s+(?:to|in|with))?\s+(.+)$/i,
      toolName: 'color_match',
      extractParams: (match) => ({
        field: 'department',
        value: match[1].trim().replace(/^(?:the|a|an)\s+/i, ''),
        color: match[2].trim().replace(/^(?:to|in|with)\s+/i, ''),
      }),
      confidence: 0.96,
    },
    {
      regex: /^color by (.+)$/i,
      toolName: 'color_by',
      extractParams: (match) => ({ field: normalizeColorByField(match[1]) }),
      confidence: 0.9,
    },
    {
      regex: /^(?:change|set)\s+(?:the\s+)?(?:fill\s+)?colou?r\s+of\s+(?:(?:the\s+)?(selected|all)\s+|this\s+)?(?:shapes?|steps?|nodes?)\s+(?:to\s+)?(.+)$/i,
      toolName: 'set_shape_color',
      extractParams: (match) => ({
        color: match[2].trim(),
        scope: match[1]?.toLowerCase() === 'all' ? 'all' : 'selected',
      }),
      confidence: 0.97,
    },
    {
      regex: /^(?:colou?r|make)\s+(?:the\s+)?(?:(selected|all|this)\s+)?(?:shapes?|steps?|nodes?)\s+(?:to\s+|in\s+|with\s+)?(.+)$/i,
      toolName: 'set_shape_color',
      extractParams: (match) => ({
        color: match[2].trim(),
        scope: match[1]?.toLowerCase() === 'all' ? 'all' : 'selected',
      }),
      confidence: 0.96,
    },
    {
      regex: /^(?:change|set)\s+(?:the\s+)?(?:fill\s+)?colou?r\s+of\s+(?:the\s+)?(.+?)\s+(?:shapes?|steps?|nodes?)\s+(?:to\s+|in\s+|with\s+)?(.+)$/i,
      toolName: 'set_shape_color',
      extractParams: (match) => extractProcessColorTargetParams(match[1], match[2]),
      confidence: 0.98,
    },
    {
      regex: /^(?:colou?r|make)\s+(?:the\s+)?(.+?)\s+(?:shapes?|steps?|nodes?)\s+(?:to\s+|in\s+|with\s+)?(.+)$/i,
      toolName: 'set_shape_color',
      extractParams: (match) => extractProcessColorTargetParams(match[1], match[2]),
      confidence: 0.97,
    },
    {
      regex: /^heatmap(?: by)? (.+)$/i,
      toolName: 'heatmap',
      extractParams: (match) => ({ field: match[1].trim() }),
      confidence: 0.85,
    },
    {
      regex: /^badge(?: by)? (.+)$/i,
      toolName: 'badge',
      extractParams: (match) => ({ field: match[1].trim() }),
      confidence: 0.85,
    },
    {
      regex: /^label(?: by)? (.+)$/i,
      toolName: 'label',
      extractParams: (match) => ({ field: match[1].trim() }),
      confidence: 0.8,
    },
    {
      regex: /^highlight (.+)$/i,
      toolName: 'highlight',
      extractParams: (match) => ({ query: match[1].trim() }),
      confidence: 0.75,
    },
    {
      regex: /^filter (?:to|by)?\s*([^=]+?)\s*(?:=|equals|is|contains)\s*(.+)$/i,
      toolName: 'filter',
      extractParams: (match) => ({
        field: match[1].trim(),
        operator: /contains/i.test(match[0]) ? 'contains' : 'equals',
        value: match[2].trim(),
      }),
      confidence: 0.85,
    },
    // ─── Network domain (Systems Mapping) ───
    {
      regex: /^(?:find|show(?: me)?|list)\s+(?:all\s+)?(?:publicly\s+)?(?:exposed|public)\s+resources?$/i,
      toolName: 'find_exposed',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    {
      regex: /^what\s+is\s+exposed$/i,
      toolName: 'find_exposed',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    {
      regex: /^(?:trace\s+)?blast\s+radius(?:\s+of\s+(.+))?$/i,
      toolName: 'trace_blast_radius',
      extractParams: (match) => (match[1] ? { target: match[1].trim() } : {}),
      confidence: 0.85,
    },
    {
      regex: /^what\s+happens\s+if\s+(.+?)\s+(?:fails|goes\s+down|dies)$/i,
      toolName: 'trace_blast_radius',
      extractParams: (match) => ({ target: match[1].trim() }),
      confidence: 0.85,
    },
    {
      regex: /^(?:find|show(?: me)?|list)\s+untagged(?:\s+resources?)?$/i,
      toolName: 'find_untagged',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    {
      regex: /^which\s+resources?\s+are\s+untagged$/i,
      toolName: 'find_untagged',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    {
      regex: /^(?:find|show(?: me)?|list)\s+(?:zombies?|abandoned|orphan(?:ed)?)(?:\s+resources?)?$/i,
      toolName: 'find_zombies',
      extractParams: () => ({}),
      confidence: 0.85,
    },
    {
      regex: /^(?:why\s+is\s+(.+?)\s+flagged|explain\s+(.+))$/i,
      toolName: 'explain_why',
      extractParams: (match) => ({ target: (match[1] ?? match[2]).trim() }),
      confidence: 0.8,
    },
    // ─── Network design-mode authoring (DL-2) ───
    // Matches phrasings like:
    //   "add a aws_ec2 called web-1"
    //   "add an aws_lambda named checkout-fn"
    //   "create aws_rds_postgres payments-db"
    //   "can you add a rds to this stack?"
    {
      regex: /^(?:can\s+you\s+|please\s+)?(?:add|create)\s+(?:an?\s+)?(?:rds(?:\s+postgres)?|postgres(?:ql)?(?:\s+database)?)(?:\s+(?:db|database))?(?:\s+(?:to|into)\s+(?:this|the|my)\s+(?:stack|architecture|diagram))?\??$/i,
      toolName: 'create_resource',
      extractParams: () => ({ resourceType: 'aws_rds_postgres', name: 'RDS Postgres' }),
      confidence: 0.88,
    },
    {
      regex: /^(?:add|create)\s+(?:an?\s+)?(aws_[a-z0-9_]+)\s+(?:called|named)\s+(.+)$/i,
      toolName: 'create_resource',
      extractParams: (match) => ({ resourceType: match[1], name: match[2].trim() }),
      confidence: 0.9,
    },
    {
      regex: /^(?:add|create)\s+(?:an?\s+)?(aws_[a-z0-9_]+)\s+(.+)$/i,
      toolName: 'create_resource',
      extractParams: (match) => ({ resourceType: match[1], name: match[2].trim() }),
      confidence: 0.8,
    },
    // "connect this to zoneA ecs" — keep the pronoun visible; connect_resources
    // will resolve and orient the architecture edge.
    {
      regex: /^(?:can\s+you\s+|please\s+)?(?:link|connect)\s+(it|this|that|selected)\s+to\s+(?:the\s+)?(.+?)\??$/i,
      toolName: 'connect_resources',
      extractParams: (match) => ({ fromId: match[1].trim(), toId: match[2].trim() }),
      confidence: 0.9,
    },
    // "connect alb-1 to ec2-1"
    {
      regex: /^connect\s+(.+?)\s+to\s+(.+)$/i,
      toolName: 'connect_resources',
      extractParams: (match) => ({ fromId: match[1].trim(), toId: match[2].trim() }),
      confidence: 0.85,
    },
    // "the zoneA ecs should use it. can you link it?"
    {
      regex: /^(?:the\s+)?(.+?)\s+should\s+(?:use|access|connect\s+to|talk\s+to|reach)\s+(it|this|that|selected)(?:[.!?]\s*)+(?:can\s+you\s+|please\s+)?(?:link|connect)(?:\s+it)?\??$/i,
      toolName: 'connect_resources',
      extractParams: (match) => ({ fromId: match[1].trim(), toId: match[2].trim() }),
      confidence: 0.9,
    },
    // "link zoneA ecs to it" / "connect app service A to selected"
    {
      regex: /^(?:can\s+you\s+|please\s+)?(?:link|connect)\s+(.+?)\s+to\s+(it|this|that|selected)\??$/i,
      toolName: 'connect_resources',
      extractParams: (match) => ({ fromId: match[1].trim(), toId: match[2].trim() }),
      confidence: 0.88,
    },
    // "remove web-1" / "delete payments-db"
    {
      regex: /^(?:remove|delete)\s+(.+)$/i,
      toolName: 'remove_resource',
      extractParams: (match) => ({ id: match[1].trim() }),
      confidence: 0.8,
    },
    // "validate (this )?(diagram|design)"
    {
      regex: /^validate\s+(?:this\s+)?(?:diagram|design)$/i,
      toolName: 'validate_diagram',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    {
      regex: /^(?:check|lint)\s+(?:this\s+|the\s+|my\s+)?(?:diagram|design)$/i,
      toolName: 'validate_diagram',
      extractParams: () => ({}),
      confidence: 0.85,
    },
    {
      regex: /^validate\s+(?:the\s+|my\s+)?(?:diagram|design)$/i,
      toolName: 'validate_diagram',
      extractParams: () => ({}),
      confidence: 0.85,
    },
    // ─── Network design-mode composition (DL-2 II) ───
    // "clone this across 3 azs" / "clone bastion across 3 regions"
    {
      regex: /^clone\s+(.+?)\s+(?:across|into)\s+(\d+)\s*(?:azs?|regions?|subnets?|times?)?$/i,
      toolName: 'clone_subtree',
      extractParams: (match) => ({ sourceId: match[1].trim(), count: parseInt(match[2], 10) }),
      confidence: 0.9,
    },
    {
      regex: /^clone\s+this\s+(?:across|into)\s+(\d+)\s*(?:azs?|regions?|subnets?|times?)?$/i,
      toolName: 'clone_subtree',
      // sourceId left empty — the LLM / panel must supply it from selectedNodeIds; tool surfaces a hint otherwise.
      extractParams: (match) => ({ count: parseInt(match[1], 10) }),
      confidence: 0.7,
    },
    // "apply the three-tier-web pattern" / "apply pattern serverless-api"
    {
      regex: /^apply\s+(?:the\s+)?([a-z][a-z0-9-]*)\s+pattern$/i,
      toolName: 'apply_pattern',
      extractParams: (match) => ({ pattern: match[1].trim() }),
      confidence: 0.9,
    },
    {
      regex: /^apply\s+pattern\s+([a-z][a-z0-9-]*)$/i,
      toolName: 'apply_pattern',
      extractParams: (match) => ({ pattern: match[1].trim() }),
      confidence: 0.9,
    },
    // "audit this design" / "audit the diagram"
    {
      regex: /^audit\s+(?:this\s+|the\s+|my\s+)?(?:design|diagram|proposed)$/i,
      toolName: 'audit_proposed',
      extractParams: () => ({}),
      confidence: 0.9,
    },
    // ─── Network computed lenses (Phase 7c) ───
    {
      regex: /^(?:lens\s+by\s+|colour\s+by\s+|color\s+by\s+)?reachability(?:\s+from\s+internet)?$/i,
      toolName: 'lens_by_reachability',
      extractParams: () => ({}),
      confidence: 0.85,
    },
    {
      regex: /^(?:lens\s+by\s+|colour\s+by\s+|color\s+by\s+)?blast\s+radius$/i,
      toolName: 'lens_by_blast_radius',
      extractParams: () => ({}),
      confidence: 0.85,
    },
    {
      regex: /^(?:lens\s+by\s+|colour\s+by\s+|color\s+by\s+)?service\s+(?:category|type)$/i,
      toolName: 'lens_by_service_category',
      extractParams: () => ({}),
      confidence: 0.85,
    },
    {
      regex: /^(?:lens\s+by\s+|colour\s+by\s+|color\s+by\s+)?(?:iac|terraform|cloudformation)\s+source$/i,
      toolName: 'lens_by_iac_source',
      extractParams: () => ({}),
      confidence: 0.85,
    },
  ];

  match(input: string, options?: StructuredMatchOptions): ToolMatch | null {
    const trimmed = input.trim();
    const priorityIndex = new Map(
      (options?.toolPriority ?? [])
        .map((toolName, index) => [String(toolName ?? '').trim(), index] as const)
        .filter(([toolName]) => toolName.length > 0),
    );
    const availableTools = new Set(
      (options?.availableTools ?? [])
        .map((toolName) => String(toolName ?? '').trim())
        .filter((toolName) => toolName.length > 0),
    );
    const highConfidenceCutoff = 0.85;
    const noPriorityRank = Number.MAX_SAFE_INTEGER;
    const matches = this.patterns
      .map((pattern) => {
        const match = trimmed.match(pattern.regex);
        if (!match) return null;
        if (availableTools.size > 0 && !availableTools.has(pattern.toolName)) return null;
        return {
          toolName: pattern.toolName,
          params: pattern.extractParams(match),
          confidence: pattern.confidence,
        } satisfies ToolMatch;
      })
      .filter(Boolean) as ToolMatch[];

    if (matches.length === 0) return null;

    matches.sort((a, b) => {
      if (a.confidence !== b.confidence) return b.confidence - a.confidence;
      if (a.confidence >= highConfidenceCutoff && b.confidence >= highConfidenceCutoff) {
        const aPriority = priorityIndex.get(a.toolName) ?? noPriorityRank;
        const bPriority = priorityIndex.get(b.toolName) ?? noPriorityRank;
        if (aPriority !== bPriority) return aPriority - bPriority;
      }
      return a.toolName.localeCompare(b.toolName);
    });
    return matches[0];
  }
}
